/**
 * The activity timeline as stories rather than rows.
 *
 * The API returns one row per event. A vendor wants to read "A visitor from
 * Lyon tried Rovere Naturale on the floor, then downloaded the result", so
 * events are grouped into visits — one visitor, no gap longer than half an
 * hour — and each visit is told as a short run of steps, with repeated tries
 * folded into one.
 *
 * Pure: the component turns the phrases into sentences with `t()`.
 */
import type { Phrase } from '../../vendor/analytics/viz/reading';

export type ActivityKind = 'try' | 'save' | 'upload' | 'shop' | 'favorite' | 'email';

/** Which API event types each filter pill asks the timeline for. */
export const KIND_EVENT_TYPES: Record<ActivityKind, string[]> = {
  try: ['ProductUsageEvent', 'CustomRugPlacedEvent'],
  save: ['ProductResultDownloadedEvent', 'ShareClickedEvent'],
  upload: ['SpaceUploadDoneEvent'],
  shop: ['ProductVisitVendorPageEvent', 'ProductOrderButtonClickEvent', 'ProductAddedToCartEvent', 'ProductRemovedFromCartEvent', 'CartCheckoutEvent', 'ProjectQuoteRequestedEvent', 'SmartAssistMarketingOptInEvent'],
  favorite: ['ProductAddedToFavoriteEvent', 'ProductRemovedFromFavoriteEvent'],
  email: ['EmailSentEvent', 'EmailOpenedEvent', 'EmailLinkClickedEvent'],
};

const KIND_OF: Record<string, ActivityKind> = Object.entries(KIND_EVENT_TYPES).reduce(
  (acc, [kind, types]) => {
    types.forEach((type) => (acc[type] = kind as ActivityKind));
    return acc;
  },
  {} as Record<string, ActivityKind>,
);

export interface TimelineRow {
  _id?: string;
  eventType: string;
  dateTime: string;
  visitorId?: string;
  deviceType?: string;
  email?: string | null;
  data?: any;
  product?: { id?: string; name?: string; thumbnailPath?: string; link?: string } | null;
  products?: Array<{ id?: string; name?: string; thumbnailPath?: string }> | null;
  visitorLocation?: { city?: string; country?: string } | null;
}

export interface FeedProduct {
  id: string;
  name: string;
  thumbnailPath?: string;
}

export type StepType =
  | 'tried'
  | 'rugPlaced'
  | 'uploaded'
  | 'uploadFailed'
  | 'downloaded'
  | 'shared'
  | 'visitedPage'
  | 'orderClick'
  | 'cartAdd'
  | 'cartRemove'
  | 'checkout'
  | 'quoteRequested'
  | 'optedIn'
  | 'favorite'
  | 'unfavorite'
  | 'emailSent'
  | 'emailOpened'
  | 'emailClicked';

export interface Step {
  type: StepType;
  kind: ActivityKind;
  at: string;
  products: FeedProduct[];
  /** The surface a single tried product went on. */
  surface?: string;
  /** The size a single carpet was placed at as a rug, in cm. */
  rugSize?: { widthCm: number; lengthCm: number };
  /** How many events this step folds together. */
  count: number;
}

export interface Session {
  id: string;
  visitorId: string;
  email?: string;
  city?: string;
  country?: string;
  device?: string;
  start: string;
  end: string;
  steps: Step[];
  kinds: ActivityKind[];
  products: FeedProduct[];
  /** Downloaded, shared, clicked to the shop or checked out: worth a look. */
  intent: boolean;
}

const STEP_OF: Record<string, StepType> = {
  ProductUsageEvent: 'tried',
  // A carpet placed as a movable rug at a custom size.
  CustomRugPlacedEvent: 'rugPlaced',
  ProductResultDownloadedEvent: 'downloaded',
  ShareClickedEvent: 'shared',
  ProductVisitVendorPageEvent: 'visitedPage',
  ProductOrderButtonClickEvent: 'orderClick',
  ProductAddedToCartEvent: 'cartAdd',
  ProductRemovedFromCartEvent: 'cartRemove',
  CartCheckoutEvent: 'checkout',
  ProjectQuoteRequestedEvent: 'quoteRequested',
  // Confirmed their email to the assistant and asked to hear from the shop.
  SmartAssistMarketingOptInEvent: 'optedIn',
  ProductAddedToFavoriteEvent: 'favorite',
  ProductRemovedFromFavoriteEvent: 'unfavorite',
  EmailSentEvent: 'emailSent',
  EmailOpenedEvent: 'emailOpened',
  EmailLinkClickedEvent: 'emailClicked',
};

const INTENT: StepType[] = ['downloaded', 'shared', 'visitedPage', 'orderClick', 'cartAdd', 'checkout', 'quoteRequested', 'optedIn'];

const time = (iso: string) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
};

function productsOf(row: TimelineRow): FeedProduct[] {
  const list: FeedProduct[] = [];
  const push = (id?: string, name?: string, thumbnailPath?: string) => {
    const key = id || name;
    if (!key || list.some((p) => p.id === key)) return;
    list.push({ id: key, name: name || '', thumbnailPath: thumbnailPath || undefined });
  };
  if (row.product) push(row.product.id, row.product.name, row.product.thumbnailPath);
  for (const p of row.products ?? []) push(p.id, p.name, p.thumbnailPath);
  if (!list.length && row.data?.productId) push(row.data.productId, row.data.productName, row.data.thumbnailPath);
  // A quote request names its products as lines of the estimate.
  if (!list.length && Array.isArray(row.data?.items)) for (const item of row.data.items) push(item?.productId);
  return list;
}

function stepOf(row: TimelineRow): Step | null {
  let type: StepType | undefined = STEP_OF[row.eventType];
  if (row.eventType === 'SpaceUploadDoneEvent') type = row.data?.successful === false ? 'uploadFailed' : 'uploaded';
  if (!type) return null;
  const surface = typeof row.data?.selectedSurface === 'string' ? row.data.selectedSurface : undefined;
  const widthCm = Number(row.data?.widthCm);
  const lengthCm = Number(row.data?.lengthCm);
  const rugSize = type === 'rugPlaced' && widthCm > 0 && lengthCm > 0 ? { widthCm, lengthCm } : undefined;
  return { type, kind: KIND_OF[row.eventType] ?? 'upload', at: row.dateTime, products: productsOf(row), surface, rugSize, count: 1 };
}

const mergeProducts = (into: FeedProduct[], more: FeedProduct[]) => {
  for (const p of more) if (!into.some((q) => q.id === p.id)) into.push(p);
};

/**
 * Events (any order) as visits, newest visit first, steps oldest first.
 * `gapMinutes` is how long a visitor may pause before it counts as a new visit.
 */
export function buildSessions(rows: TimelineRow[], gapMinutes = 30): Session[] {
  const gap = gapMinutes * 60_000;
  const sorted = [...rows].filter((r) => r && r.dateTime).sort((a, b) => time(a.dateTime) - time(b.dateTime));
  const open = new Map<string, Session>();
  const sessions: Session[] = [];

  for (const row of sorted) {
    const step = stepOf(row);
    if (!step) continue;
    const who = row.visitorId || row.email || row._id || 'unknown';
    let session = open.get(who);
    if (!session || time(row.dateTime) - time(session.end) > gap) {
      session = {
        id: `${who}-${row.dateTime}`,
        visitorId: who,
        start: row.dateTime,
        end: row.dateTime,
        steps: [],
        kinds: [],
        products: [],
        intent: false,
      };
      open.set(who, session);
      sessions.push(session);
    }
    session.end = row.dateTime;
    if (row.email && !session.email) session.email = row.email;
    if (!session.email && typeof row.data?.userEmail === 'string' && row.data.userEmail) session.email = row.data.userEmail;
    if (row.visitorLocation?.city && !session.city) session.city = row.visitorLocation.city;
    if (row.visitorLocation?.country && !session.country) session.country = row.visitorLocation.country;
    if (row.deviceType && !session.device) session.device = row.deviceType;

    const last = session.steps[session.steps.length - 1];
    // Ten tries in a row read as one step; so do repeated downloads.
    if (last && last.type === step.type) {
      last.count += 1;
      last.at = step.at;
      mergeProducts(last.products, step.products);
      if (last.surface !== step.surface) last.surface = undefined;
      if (last.rugSize?.widthCm !== step.rugSize?.widthCm || last.rugSize?.lengthCm !== step.rugSize?.lengthCm) last.rugSize = undefined;
    } else {
      session.steps.push(step);
    }
    if (!session.kinds.includes(step.kind)) session.kinds.push(step.kind);
    mergeProducts(session.products, step.products);
    if (INTENT.includes(step.type)) session.intent = true;
  }

  return sessions.sort((a, b) => time(b.end) - time(a.end));
}

const SURFACE_KEY: Record<string, string> = { floor: 'FLOOR', interactive_floor: 'FLOOR', walls: 'WALLS', wall: 'WALLS', ceiling: 'CEILING' };

/** Who did it, as the sentence's subject. Keys are relative to `VIZ_ANALYTICS.ACTIVITY.STORY`. */
export function subjectPhrase(session: Pick<Session, 'email' | 'city' | 'country'>): Phrase {
  if (session.email) return { key: 'SUBJECT_EMAIL', params: { email: session.email } };
  if (session.city) return { key: 'SUBJECT_PLACE', params: { place: session.city } };
  if (session.country) return { key: 'SUBJECT_PLACE', params: { place: session.country } };
  return { key: 'SUBJECT_ANON' };
}

/** One step as a verb phrase: "tried Rovere Naturale on the floor". `unnamed` stands in for a product with no name. */
export function stepPhrase(step: Step, unnamed: string): Phrase {
  const names = step.products.map((p) => p.name || unnamed);
  const first = names[0] ?? unnamed;
  switch (step.type) {
    case 'tried': {
      if (names.length >= 3) return { key: 'TRIED_MANY', params: { product: first, count: names.length - 1 } };
      if (names.length === 2) return { key: 'TRIED_TWO', params: { first, second: names[1] } };
      const surface = step.surface ? SURFACE_KEY[step.surface] : undefined;
      return surface ? { key: `TRIED_ON_${surface}`, params: { product: first } } : { key: 'TRIED_ONE', params: { product: first } };
    }
    case 'rugPlaced': {
      if (names.length >= 2) return { key: 'RUG_PLACED_MANY', params: { product: first, count: names.length - 1 } };
      const size = step.rugSize;
      return size
        ? { key: 'RUG_PLACED_SIZE', params: { product: first, width: Math.round(size.widthCm), length: Math.round(size.lengthCm) } }
        : { key: 'RUG_PLACED', params: { product: first } };
    }
    case 'downloaded':
      return step.count > 1 ? { key: 'DOWNLOADED_MANY', params: { count: step.count } } : { key: 'DOWNLOADED' };
    case 'shared':
      return { key: 'SHARED' };
    case 'uploaded':
      return { key: 'UPLOADED' };
    case 'uploadFailed':
      return { key: 'UPLOAD_FAILED' };
    case 'visitedPage':
      return { key: 'VISITED_PAGE', params: { product: first } };
    case 'orderClick':
      return { key: 'ORDER_CLICK', params: { product: first } };
    case 'cartAdd':
      return { key: 'CART_ADD', params: { product: first } };
    case 'cartRemove':
      return { key: 'CART_REMOVE', params: { product: first } };
    case 'checkout':
      return { key: 'CHECKOUT' };
    case 'quoteRequested':
      return { key: 'QUOTE_REQUESTED', params: { product: first } };
    case 'optedIn':
      return { key: 'OPTED_IN' };
    case 'favorite':
      return { key: 'FAVORITE', params: { product: first } };
    case 'unfavorite':
      return { key: 'UNFAVORITE', params: { product: first } };
    case 'emailSent':
      return { key: 'EMAIL_SENT' };
    case 'emailOpened':
      return { key: 'EMAIL_OPENED' };
    case 'emailClicked':
      return { key: 'EMAIL_CLICKED' };
    default:
      return { key: 'DID_SOMETHING' };
  }
}

/** The steps a sentence names before "and N more". */
export const STORY_STEPS = 3;

/** Whether a visit is still going: its last step within `minutes`. */
export function isLive(session: Pick<Session, 'end'>, now: Date = new Date(), minutes = 5): boolean {
  return now.getTime() - time(session.end) <= minutes * 60_000;
}
